import React, { type ReactElement } from 'react';
import { toInteger } from 'lodash';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from 'atom-components/error-message-from-hook-form';
import SingleComboBox, {
  type ComboBoxItem
} from 'atom-components/form-fields-for-hook-form/single-combo-box';
import Label from 'atom-components/label';
import SelectableMarkerBadge from 'feature-components/badges/marker-badges/selectable-marker-badge';
import { friendlyMarkerName } from 'helpers/domain-specific/marker.helpers';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import styles from './active-marker.module.scss';

type Props = {
  organisationUsers: BaseCustomerUser[];
};

export default function ActiveMarker({ organisationUsers }: Props): ReactElement {
  const { control, setValue, formState } = useFormContext();
  const { errors } = formState;

  /**
   * Note: the user is choosing from a list of organisation users here. Not all of
   * these users can be considered be markers yet since some may not have had
   * responses allocated to them.
   */
  const selectableOrganisationUsers: ComboBoxItem[] = organisationUsers.map((organisationUser) => ({
    id: organisationUser.id,
    name: friendlyMarkerName(organisationUser),
    badge: <SelectableMarkerBadge organisationUser={organisationUser} warnIfNotCurrentUser />
  }));

  const fieldName = 'organisationUserId';

  return (
    <div className={styles.activeMarkerContainer}>
      <Label htmlFor={fieldName} required>
        Active Marker
      </Label>
      <SingleComboBox
        id={fieldName}
        name={fieldName}
        control={control}
        allItems={selectableOrganisationUsers}
        clearable={false}
        onChange={(item): void => {
          // Organisation User ID should always be an integer when the field is
          // changed. It is impossible to set the active marker back to
          // undefined to prevent orphan marks.
          const organisationUserId = toInteger(item?.id);
          setValue(fieldName, organisationUserId, {
            shouldValidate: true,
            shouldTouch: true
          });
        }}
      />
      {errors.organisationUserId && <ErrorMessage errors={errors} name={fieldName} />}
    </div>
  );
}
